import { useEffect, useState } from "react";
import { findByInviteCode, saveMember, setTeammateId, type TeamMember } from "../lib/team";
import { isCloud } from "../lib/supabase";
import { signUp, signIn } from "../lib/auth";
import { Brand } from "./Brand";

/**
 * The screen a teammate lands on from their invite link (?invite=CODE).
 * They confirm their name (and set a password in cloud mode), which activates
 * their seat and drops them into their teammate workspace.
 */
export function AcceptInvite({ code, onAccepted, onCancel }: { code: string; onAccepted: () => void; onCancel: () => void }) {
  const [member, setMember] = useState<TeamMember | null | undefined>(undefined);
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { findByInviteCode(code).then(setMember).catch(() => setMember(null)); }, [code]);

  async function accept() {
    if (!member) return;
    setBusy(true);
    setErr(null);
    try {
      if (isCloud()) {
        if (pw.length < 6) { setErr("Choose a password of at least 6 characters."); setBusy(false); return; }
        try {
          const { needsConfirm } = await signUp(member.email, pw);
          if (needsConfirm) { setErr("Account created — check your email to confirm, then open this link again to sign in."); setBusy(false); return; }
        } catch (e) {
          // already registered → try signing in
          try { await signIn(member.email, pw); } catch { setErr(e instanceof Error ? e.message : "Could not sign in."); setBusy(false); return; }
        }
      }
      await saveMember({ ...member, name: name.trim() || member.name, status: "active" });
      setTeammateId(member.id);
      onAccepted();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Something went wrong.");
      setBusy(false);
    }
  }

  return (
    <div className="invite-gate">
      <div className="card pad invite-card">
        <Brand className="lp-brand" />

        {member === undefined ? (
          <p style={{ color: "var(--muted)" }}>Checking your invite…</p>
        ) : member === null ? (
          <>
            <h2 className="fr" style={{ color: "var(--forest)", margin: "0 0 6px" }}>Invite not found</h2>
            <p style={{ color: "var(--muted)", fontSize: 14 }}>This link may have expired or been revoked. Ask whoever invited you to send a fresh one.</p>
            <button className="btn btn-g btn-sm" style={{ marginTop: 12 }} onClick={onCancel}>Go to Beacon</button>
          </>
        ) : (
          <>
            <div className="eyebrow" style={{ marginBottom: 6 }}>You're invited</div>
            <h2 className="fr" style={{ color: "var(--forest)", margin: "0 0 6px", fontSize: 24 }}>Join as {member.role}</h2>
            <p style={{ color: "#37413c", fontSize: 14, marginBottom: 14 }}>
              You'll get your own seat that shows only the properties you're assigned — and lets you submit your
              numbers in the owner's format. Let's set you up.
            </p>
            <label className="bb-f" style={{ marginBottom: 10 }}><span>Your name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Joe from Joe's Roofing" /></label>
            <label className="bb-f" style={{ marginBottom: 10 }}><span>Email</span>
              <input value={member.email} disabled style={{ opacity: 0.7 }} /></label>
            {isCloud() && (
              <label className="bb-f" style={{ marginBottom: 10 }}><span>Choose a password</span>
                <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="at least 6 characters" /></label>
            )}
            {err && <div className="flag warn" style={{ marginBottom: 10 }}><span>◐</span><div>{err}</div></div>}
            <button className="btn btn-p" onClick={accept} disabled={busy}>{busy ? "Setting up…" : "Accept & continue →"}</button>
            <button className="btn btn-g btn-sm" style={{ marginLeft: 8 }} onClick={onCancel}>Cancel</button>
          </>
        )}
      </div>
    </div>
  );
}
